// Protocolo do modo online, compartilhado entre o cliente web e o servidor.
// Toda mensagem que chega ao servidor é validada com estes esquemas zod.

import { z } from 'zod';
import type { Action, Addons, BotDifficulty, GameEvent, PlayerView, VocationId } from '@estandarte/engine';

export const MAX_MESSAGE_BYTES = 16 * 1024;
export const ROOM_CODE_ALPHABET = '23456789ABCDEFGHJKMNPQRSTUVWXYZ'; // sem 0/O, 1/I/L
export const ROOM_CODE_LENGTH = 6;
export const MAX_SEATS = 6;
export const MIN_SEATS = 3;
export const MAX_BOTS = 5;

const id = z.string().min(1).max(32);
const name = z
  .string()
  .trim()
  .min(1)
  .max(20)
  .refine((s) => !/[\u0000-\u001f]/.test(s), 'caracteres inválidos');
const password = z.string().max(64);
const vocation = z.enum(['defense', 'range', 'damage', 'heal', 'harmony']);
const difficulty = z.enum(['easy', 'normal']);
const loot = z.enum([
  'strike',
  'barrage',
  'strength',
  'precision',
  'blessing',
  'wall',
  'restore',
  'ward',
  'paralyze',
  'luck',
  'captain',
  'teleport',
  'reinforce',
  'draw',
  'steal',
]);
const count = z.number().int().min(0).max(10_000);
const player = z.number().int().min(0).max(MAX_SEATS - 1);

export const actionSchema: z.ZodType<Action> = z.discriminatedUnion('type', [
  z.object({ type: z.literal('place'), player, territory: id, count }),
  z.object({ type: z.literal('autoSetup'), player }),
  z.object({ type: z.literal('trade'), player, cards: z.array(id).length(3) }),
  z.object({ type: z.literal('endPhase'), player }),
  z.object({ type: z.literal('attack'), player, from: id, to: id, untilEnd: z.boolean().optional() }),
  z.object({ type: z.literal('attackBoss'), player, from: id }),
  z.object({ type: z.literal('moveIn'), player, count }),
  z.object({ type: z.literal('fortify'), player, from: id, to: id, count }),
  z.object({ type: z.literal('power'), player, target: id }),
  z.object({ type: z.literal('heal'), player, territory: id }),
  z.object({
    type: z.literal('playLoot'),
    player,
    kind: loot,
    territory: id.optional(),
    from: id.optional(),
    to: id.optional(),
    count: count.optional(),
    targets: z.array(id).max(3).optional(),
    victim: player.optional(),
  }),
  z.object({ type: z.literal('discardLoot'), player, kind: loot }),
  z.object({ type: z.literal('chooseLoot'), player, kind: loot }),
  z.object({ type: z.literal('chooseBossRegion'), player, region: id }),
  z.object({ type: z.literal('react'), player, use: z.boolean() }),
]) as unknown as z.ZodType<Action>;

const addons = z.object({ sea: z.boolean(), vocations: z.boolean(), bosses: z.boolean() });

export const clientMessageSchema = z.discriminatedUnion('t', [
  z.object({ t: z.literal('create'), name, password: password.optional() }),
  z.object({ t: z.literal('join'), code: z.string().trim().toUpperCase().pipe(z.string().length(ROOM_CODE_LENGTH)), name, password: password.optional() }),
  z.object({ t: z.literal('resume'), token: z.string().min(10).max(80) }),
  z.object({ t: z.literal('leave') }),
  z.object({ t: z.literal('seat'), color: id.optional(), vocation: vocation.optional() }),
  z.object({ t: z.literal('addons'), addons }),
  /** Era da partida (id das crônicas do tema; null = regras clássicas). */
  z.object({ t: z.literal('era'), era: id.nullable() }),
  z.object({ t: z.literal('addBot'), difficulty: difficulty.optional() }),
  z.object({
    t: z.literal('botSeat'),
    seat: id,
    color: id.optional(),
    vocation: vocation.optional(),
    difficulty: difficulty.optional(),
  }),
  z.object({ t: z.literal('removeSeat'), seat: id }),
  z.object({ t: z.literal('start') }),
  z.object({ t: z.literal('action'), action: actionSchema }),
  z.object({ t: z.literal('ping') }),
]);

export type ClientMessage = z.infer<typeof clientMessageSchema>;

export interface LobbySeat {
  id: string;
  name: string;
  kind: 'human' | 'bot';
  color: string;
  vocation: VocationId;
  difficulty: BotDifficulty;
  connected: boolean;
  isHost: boolean;
}

export interface LobbyState {
  code: string;
  hasPassword: boolean;
  addons: Addons;
  era: string | null;
  seats: LobbySeat[];
  started: boolean;
}

export interface GameSeat {
  id: string;
  name: string;
  kind: 'human' | 'bot';
  connected: boolean;
  /** Humano ausente cujo turno está sendo jogado por um bot. */
  botControlled: boolean;
}

export type ServerMessage =
  | { t: 'welcome'; token: string; code: string; seat: string; host: boolean }
  | { t: 'lobby'; lobby: LobbyState }
  | { t: 'game'; view: PlayerView; me: number; seats: GameSeat[]; events: GameEvent[]; host: boolean }
  | { t: 'error'; code: string; message?: string }
  | { t: 'kicked' }
  | { t: 'left' }
  /** A mesma sessão foi aberta em outra conexão (outra aba). */
  | { t: 'replaced' }
  | { t: 'pong' };

export type ErrorCode =
  | 'room_not_found'
  | 'wrong_password'
  | 'room_full'
  | 'game_started'
  | 'name_taken'
  | 'rate_limited'
  | 'not_host'
  | 'color_taken'
  | 'invalid_message'
  | 'not_enough_players'
  | 'too_many_bots'
  | 'bad_token'
  | 'not_in_room';

export function parseClientMessage(raw: string): ClientMessage | null {
  let data: unknown;
  try {
    data = JSON.parse(raw);
  } catch {
    return null;
  }
  const res = clientMessageSchema.safeParse(data);
  return res.success ? res.data : null;
}

export type { Addons };
